import React from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

/**
 * Last-resort React error boundary so an exception during render does not
 * leave the user staring at the dev red-screen. Shows a calm Italian-only
 * fallback with a Retry action.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Best-effort console log for diagnosis, swallowed otherwise.
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info?.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || "—";
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center px-4"
        data-testid="error-boundary"
      >
        <div className="surface-card p-6 sm:p-8 max-w-md w-full text-center">
          <div className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-[#FDF0EC] text-[#B91C1C]">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h2 className="mt-4 font-heading text-2xl font-bold text-[#0f172a]">
            Qualcosa è andato storto
          </h2>
          <p className="mt-2 text-sm text-[#475569]">
            La pagina ha riscontrato un imprevisto. Puoi riprovare oppure tornare alla
            panoramica.
          </p>
          <details className="mt-3 text-left text-[11px] text-[#94A99B] cursor-pointer">
            <summary>Dettagli tecnici</summary>
            <pre className="mt-1 whitespace-pre-wrap break-words">{msg}</pre>
          </details>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            <button
              type="button"
              onClick={this.reset}
              data-testid="error-boundary-retry"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#102A43] text-white text-sm font-medium hover:bg-[#0b1b2c]"
            >
              <RotateCcw className="w-4 h-4" />
              Riprova
            </button>
            <a
              href="/dashboard"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-[#E2E8F0] text-sm font-medium text-[#475569] hover:border-[#102A43] hover:text-[#102A43]"
            >
              Vai alla Panoramica
            </a>
          </div>
        </div>
      </div>
    );
  }
}
